import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { ShoppingBasket } from "lucide-react";

import { listTraderRepresentatives, listTraderRepresentativesReset } from "../../actions";
import { PageBreadcrumb } from "../../components/PageBreadcrumb";
import { type TraderRepresentativeItem } from "../TraderDetailsModal/components/RepresentativesTable";
import { RepresentativesTable } from "./RepresentativesTable";
import { getCountryObjectFromCache } from "@/utils";

const ALL_ITEMS_PAGE_SIZE = 9999;

const NO_REPRESENTATIVES: TraderRepresentativeItem[] = [];

interface RootState {
  listTraderRepresentatives: {
    data: { results?: TraderRepresentativeItem[] };
    isFetching: boolean;
  };
}

export const BuyerRepresentatives = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<Dispatch<UnknownAction>>();

  const representatives = useSelector(
    (state: RootState) => state.listTraderRepresentatives.data.results ?? NO_REPRESENTATIVES
  );
  const countryName = getCountryObjectFromCache()?.name ?? "";
  const isFetching = useSelector((state: RootState) => state.listTraderRepresentatives.isFetching);

  useEffect(() => {
    dispatch(
      listTraderRepresentatives({ page_size: ALL_ITEMS_PAGE_SIZE }) as unknown as UnknownAction
    );
    return () => {
      dispatch(listTraderRepresentativesReset() as unknown as UnknownAction);
    };
  }, [dispatch]);

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("header.overview"), href: "/wfpManager" },
            { label: t("home.manager.overview.buyerRepresentativesModal.breadcrumb") },
          ]}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-green-600 bg-green-100">
              <ShoppingBasket />
            </span>
            <h1 className="font-semibold text-2xl">
              {t("home.manager.overview.buyerRepresentativesModal.title", { country: countryName })}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {t("home.manager.overview.buyerRepresentativesModal.description")}
          </p>
        </div>
      </div>

      <RepresentativesTable
        representatives={representatives}
        isFetchingRepresentatives={isFetching}
      />
    </main>
  );
};
